/**
 * ServiceTypePicker.jsx
 * Grid modal for selecting a service type (Ticketing, Visa, Hotel, etc.)
 * Fetches from /api/bulk-sales/service-types and falls back to defaults.
 */
import React, { useEffect, useState } from "react";
import {
  FaTimes, FaPlane, FaPassport, FaHotel, FaShieldAlt,
  FaSuitcaseRolling, FaCommentDots, FaHandsHelping,
  FaBuilding, FaStar, FaMosque, FaRoute, FaLock,
  FaEllipsisH, FaTag, FaSearch
} from "react-icons/fa";
import "./BulkSales.css";

const ICON_MAP = {
  FaPlane:           FaPlane,
  FaPassport:        FaPassport,
  FaHotel:           FaHotel,
  FaShieldAlt:       FaShieldAlt,
  FaSuitcaseRolling: FaSuitcaseRolling,
  FaCommentDots:     FaCommentDots,
  FaHandsHelping:    FaHandsHelping,
  FaBuilding:        FaBuilding,
  FaStar:            FaStar,
  FaMosque:          FaMosque,
  FaRoute:           FaRoute,
  FaLock:            FaLock,
  FaEllipsisH:       FaEllipsisH,
  FaTag:             FaTag,
};

const FALLBACK_TYPES = [
  { id: 1,  name: "Air Ticket",        icon_name: "FaPlane" },
  { id: 2,  name: "Ticketing",         icon_name: "FaPlane" },
  { id: 3,  name: "Visa",              icon_name: "FaPassport" },
  { id: 4,  name: "Hotel",             icon_name: "FaHotel" },
  { id: 5,  name: "Insurance",         icon_name: "FaShieldAlt" },
  { id: 6,  name: "Baggage",           icon_name: "FaSuitcaseRolling" },
  { id: 7,  name: "Visa Message",      icon_name: "FaCommentDots" },
  { id: 8,  name: "Visa Assistance",   icon_name: "FaHandsHelping" },
  { id: 9,  name: "Real Estate",       icon_name: "FaBuilding" },
  { id: 10, name: "Attractions",       icon_name: "FaStar" },
  { id: 11, name: "Umrah Service",     icon_name: "FaMosque" },
  { id: 12, name: "B2B Khasab",        icon_name: "FaRoute" },
  { id: 13, name: "Security Deposit",  icon_name: "FaLock" },
  { id: 99, name: "Others",            icon_name: "FaEllipsisH" },
];

export default function ServiceTypePicker({ onSelect, onClose }) {
  const [types, setTypes]   = useState(FALLBACK_TYPES);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetch("/api/bulk-sales/service-types")
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (Array.isArray(data) && data.length > 0) setTypes(data);
      })
      .catch(() => {}); // silently fall back to defaults
  }, []);

  const filtered = types.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div
      className="bs-svc-overlay"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bs-svc-modal">
        <div className="bs-svc-modal-header">
          <span>Select Service Type</span>
          <button className="bs-svc-close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: "14px 20px 0", borderBottom: "1px solid var(--bs-border)" }}>
          <div style={{ position: "relative" }}>
            <FaSearch style={{
              position: "absolute", left: 10, top: "50%",
              transform: "translateY(-50%)", color: "var(--bs-muted)", fontSize: 12
            }} />
            <input
              className="bs-form-input"
              style={{ paddingLeft: 30, marginBottom: 14 }}
              placeholder="Search service types…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>
        </div>

        {/* Grid */}
        <div className="bs-svc-grid">
          {filtered.map(type => {
            const Icon = ICON_MAP[type.icon_name] || FaTag;
            return (
              <div
                key={type.id}
                className="bs-svc-tile"
                onClick={() => onSelect(type)}
                title={type.description || type.name}
              >
                <div className="bs-svc-tile-icon">
                  <Icon />
                </div>
                <div className="bs-svc-tile-name">{type.name}</div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div style={{
              gridColumn: "1/-1", textAlign: "center",
              color: "var(--bs-muted)", padding: "32px 0", fontSize: 13
            }}>
              No service types match "{search}"
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
